/**
 * **金额展示 —— 端侧唯一的金额渲染处**
 *
 * ## 🔴 三条硬纪律
 *
 * 1. **不在端侧做除法**（规格 §22.1 禁浮点）。`cents / 100` 在 JS 里会产生
 *    `12.340000000000002` 这类值，而**全项目禁除法**正是为了消灭这种漂移 ——
 *    故一律走共享内核 `formatMoneyText()`（内部经 `centsToYuanText()` 走定点整除）。
 * 2. **红涨绿跌**（中国约定，与欧美相反）。颜色从令牌取（`theme.jiaying.money.*` 经
 *    MUI 的 `success`/`error` 槽位映射，见 `theme/theme.ts`），**不写字面量色值**。
 * 3. 🔴 **币种必填**（`DR-036` D1）。`DR-004` 定「余额永远**原币**整数分、不跨币相加」
 *    ⇒ 拿不到币种的金额组件**本身就是不完整的**（也才无从判断两个数能不能比、能不能相加）。
 *
 * ## 🔴 本批（㉘）换了什么
 *
 * 上一版的 `¥` 是**写死**的，且只有一个 `symbol?: boolean` 开关 —— 于是美元账户的余额
 * 会被渲染成人民币的样子。现在：
 *
 * | 之前 | 现在 |
 * |---|---|
 * | `symbol ? '¥' : ''`（写死人民币） | `formatMoneyText(cents, currency, …)`（符号表在共享层） |
 * | 无 `currency` 参数 | `currency: string` **必填** ⇒ 漏传是**编译错误**，不是静默的 `¥` |
 * | 正数无符号 | `signed` 显式开启时正数带 `+`（收入）；绝对额保持不带 |
 * | `symbol={false}` 开关（**从未被任何调用点用过**） | 已删除 —— 它是一道"渲染无币种金额"的后门，与 D1 相冲 |
 *
 * ## 为什么单独一个组件而不是各处写 `¥{…}`
 * 因为"符号 + 千分位 + 正负着色 + 等宽字体"这四件事一旦各写各的，金额看起来就会不一致
 * （而这种不一致在财务界面里**看起来像数据错误**）。单点收口后，改一次全站生效。
 */

import { Typography, type TypographyProps } from '@mui/material';
import { formatMoneyText, type Cents } from '@jiaying/shared';

export interface AmountProps extends Omit<TypographyProps, 'children'> {
  cents: Cents;
  /** 🔴 **金额所属币种**（ISO 4217；余额/净值用账本 `baseCurrency`、流水行用腿所属账户币种） */
  currency: string;
  /** 是否按正负着色（默认 true；标题里的合计常不需要） */
  colored?: boolean;
  /**
   * **有方向的金额**（收入 / 支出）⇒ 正数显示 `+`（`DR-036` D4）。
   *
   * 🔴 默认 `false`：净资产 / 总资产 / 余额 / 保额这类**绝对额**加 `+` 是噪音
   * （`¥+9,700.00` 读起来像"增加了 9700"）。设计原型对"选中合计"同样是显式传参才加。
   */
  signed?: boolean;
}

export function Amount({ cents, currency, colored = true, signed = false, sx, ...rest }: AmountProps) {
  const n = cents as number;
  const color = !colored || n === 0 ? 'text.primary' : n > 0 ? 'success.main' : 'error.main';
  return (
    <Typography
      {...rest}
      sx={{
        fontFamily: (t) => t.jiaying.font.num,
        // 等宽数字：多行金额右对齐时才不会"跳位"
        fontVariantNumeric: 'tabular-nums',
        color,
        ...sx,
      }}
    >
      {formatMoneyText(cents, currency, { signed })}
    </Typography>
  );
}
